import { Component } from '@theme/component';
import { isVisibleForOptions } from '@theme/aromely-option-tags';

/**
 * @typedef {object} CardGalleryImage
 * @property {string} src - 600w image URL
 * @property {string} srcset - 200–1200w srcset string (mirrors aromely-card-image)
 * @property {string} alt - Tag-stripped alt text
 * @property {string[]} tags - `optionIndex:value` tag tokens ([] = universal image)
 */

/**
 * Aromely Card Color Swatches.
 *
 * PLP product-card color selector: clicking a swatch swaps the card gallery
 * images to the ones tagged for that color ([tag] alt convention, see
 * snippets/aromely-card-image-pick.liquid), updates the selected ring, and
 * re-points the card links at the variant URL. Image data comes from the
 * [data-card-gallery-images] JSON blob the gallery emits in metafield mode;
 * without it only the ring and links update.
 *
 * @extends {Component<{}>}
 */
class AromelyCardSwatches extends Component {
  /**
   * Swatch click — swap gallery images, selection ring, and card links.
   * @param {Event} event
   */
  select(event) {
    const button = /** @type {HTMLElement|null} */ (
      event.target instanceof Element ? event.target.closest('button[data-color-handle]') : null
    );
    if (!button) return;

    this.#updateSelection(button);

    const card = this.closest('product-card') || this.closest('.aromely-product-card');
    if (!card) return;

    this.#updateLinks(card, button.dataset.variantUrl);
    this.#swapImages(card, button.dataset.optionHandles);
  }

  /** @param {HTMLElement} selected */
  #updateSelection(selected) {
    for (const button of this.querySelectorAll('button[data-color-handle]')) {
      const isSelected = button === selected;
      button.classList.toggle('border-[#000000]', isSelected);
      button.classList.toggle('border-[#e5e5e5]', !isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    }
  }

  /**
   * @param {Element} card
   * @param {string} [url]
   */
  #updateLinks(card, url) {
    if (!url) return;
    for (const link of card.querySelectorAll('.product-card__link, .aromely-product-gallery a[href]')) {
      if (link instanceof HTMLAnchorElement) link.href = url;
    }
  }

  /**
   * @param {Element} card
   * @param {string} [optionHandles] - Space-separated handleized option values, by position
   */
  #swapImages(card, optionHandles) {
    if (!optionHandles) return;
    const handles = optionHandles.trim().split(/\s+/);
    const blob = card.querySelector('[data-card-gallery-images]');
    if (!blob) return;

    /** @type {CardGalleryImage[]} */
    let images = [];
    try {
      images = JSON.parse(blob.textContent || '[]');
    } catch {
      return;
    }

    // Universal images (no tags) show for every variant. Zero matches keeps
    // the current images — better than an empty card while content is WIP.
    const visible = images.filter((image) => isVisibleForOptions(image.tags, handles));
    const slides = card.querySelectorAll('.aromely-product-gallery .slyde-wrapper img');
    if (!visible.length || !slides.length) return;

    slides.forEach((img, index) => {
      const pick = visible[index % visible.length];
      if (!(img instanceof HTMLImageElement)) return;
      img.src = pick.src;
      img.srcset = pick.srcset;
      img.alt = pick.alt;
    });

    const slydeShow = card.querySelector('slyde-show');
    if (slydeShow && typeof (/** @type {any} */ (slydeShow)).goTo === 'function') {
      /** @type {any} */ (slydeShow).goTo(0);
    }
  }
}

if (!customElements.get('aromely-card-swatches')) {
  customElements.define('aromely-card-swatches', AromelyCardSwatches);
}
